import type { AppEntry } from '@/apps/useApps';

/**
 * Coincide si la query (case-insensitive, trimmed) aparece en nombre o descripción.
 * Una query vacía deja pasar todo.
 */
export function matchesSearch(app: AppEntry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (app.name.toLowerCase().includes(q)) return true;
  if (app.description && app.description.toLowerCase().includes(q)) return true;
  return false;
}

/** Coincide si la app pertenece a la categoría seleccionada (null = todas). */
export function matchesCategory(app: AppEntry, category: string | null): boolean {
  if (!category) return true;
  return app.category === category;
}

/** Coincide si la app pertenece a la subcategoría seleccionada (null = todas). */
export function matchesSubcategory(app: AppEntry, subcategory: string | null): boolean {
  if (!subcategory) return true;
  return app.subcategory === subcategory;
}

/**
 * Identifica las apps de tipo "agente GPT" por su categoría — el mismo campo que
 * usa el filtro del portal. Se consideran GPT las apps cuya `category` contiene
 * "GPT" (case-insensitive): cubre "GPT", "GPTs", "Agentes GPT", etc. Si en el
 * catálogo la categoría de los GPT fuese otra, basta ajustar esta condición.
 */
export function isGptApp(app: Pick<AppEntry, 'category'>): boolean {
  return (app.category ?? '').toLowerCase().includes('gpt');
}

/** Categorías distintas presentes en el catálogo, ordenadas alfabéticamente. */
export function distinctCategories(apps: readonly AppEntry[]): string[] {
  const set = new Set<string>();
  for (const a of apps) if (a.category) set.add(a.category);
  return [...set].sort((x, y) => x.localeCompare(y));
}

/** Subcategorías distintas; si se pasa una categoría, solo las de esa categoría. */
export function distinctSubcategories(
  apps: readonly AppEntry[],
  category: string | null,
): string[] {
  const set = new Set<string>();
  for (const a of apps) {
    if (category && a.category !== category) continue;
    if (a.subcategory) set.add(a.subcategory);
  }
  return [...set].sort((x, y) => x.localeCompare(y));
}

export function paginate<T>(items: readonly T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function totalPages(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}
